import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTheme } from '../ThemeContext';
import { useToast } from './Toast';
import { getMe, changePassword, deleteAccount } from '../api';

export default function ProfileMenu() {
  const { colors, common } = useTheme();
  const navigate = useNavigate();
  const toast = useToast();

  const [email, setEmail] = useState('');
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState(null); // 'changePassword' | 'deleteAccount'

  // Change password form state
  const [currentPw, setCurrentPw]     = useState('');
  const [newPw, setNewPw]             = useState('');
  const [confirmPw, setConfirmPw]     = useState('');
  const [pwError, setPwError]         = useState('');
  const [pwLoading, setPwLoading]     = useState(false);

  // Delete account form state
  const [deletePw, setDeletePw]         = useState('');
  const [deleteError, setDeleteError]   = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  const menuRef = useRef(null);

  useEffect(() => {
    getMe().then(({ data }) => setEmail(data.email)).catch(() => {});
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close dropdown on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const closeModal = () => {
    setModal(null);
    setPwError(''); setCurrentPw(''); setNewPw(''); setConfirmPw('');
    setDeleteError(''); setDeletePw('');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwError('');
    if (newPw !== confirmPw) { setPwError('New passwords do not match.'); return; }
    if (newPw.length < 8) { setPwError('New password must be at least 8 characters.'); return; }
    setPwLoading(true);
    try {
      await changePassword({ currentPassword: currentPw, newPassword: newPw });
      toast('Password changed successfully.', 'success');
      closeModal();
    } catch (err) {
      setPwError(err.response?.data?.error || 'Could not change password. Please try again.');
    }
    setPwLoading(false);
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    setDeleteError('');
    setDeleteLoading(true);
    try {
      await deleteAccount({ password: deletePw });
      localStorage.removeItem('token');
      navigate('/login');
    } catch (err) {
      setDeleteError(err.response?.data?.error || 'Could not delete account. Please try again.');
      setDeleteLoading(false);
    }
  };

  const initial = email ? email[0].toUpperCase() : '?';

  // ── Styles ────────────────────────────────────────────────────────────────
  const avatarBtn = {
    width: 36, height: 36, borderRadius: '50%',
    background: `linear-gradient(135deg, ${colors.indigo}, #7c3aed)`,
    color: '#fff', fontWeight: 700, fontSize: 15,
    border: 'none', cursor: 'pointer', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 2px 8px rgba(79,70,229,0.35)',
  };

  const dropdown = {
    position: 'absolute', top: 44, right: 0, zIndex: 200,
    backgroundColor: colors.cardBg, border: `1px solid ${colors.borderColor}`,
    boxShadow: '0 8px 32px rgba(0,0,0,0.25)', borderRadius: 12,
    minWidth: 230, overflow: 'hidden',
  };

  const menuItem = (danger) => ({
    display: 'block', width: '100%', textAlign: 'left',
    padding: '10px 18px', border: 'none', background: 'transparent',
    color: danger ? '#ef4444' : colors.textPrimary,
    fontSize: 14, cursor: 'pointer', fontWeight: 500,
    fontFamily: 'inherit',
  });

  return (
    <div ref={menuRef} style={{ position: 'relative' }}>

      {/* ── Avatar button ── */}
      <button
        onClick={() => setOpen((p) => !p)}
        aria-label="Open account menu"
        aria-expanded={open}
        aria-haspopup="true"
        style={avatarBtn}
        title={email || 'Account'}
      >
        {initial}
      </button>

      {/* ── Dropdown ── */}
      {open && (
        <div style={dropdown} role="menu">
          {/* Email header */}
          <div style={{ padding: '14px 18px 12px', borderBottom: `1px solid ${colors.borderColor}` }}>
            <p style={{ margin: 0, fontSize: 11, color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Signed in as
            </p>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: colors.textPrimary, fontWeight: 600, wordBreak: 'break-all' }}>
              {email || '…'}
            </p>
          </div>

          {/* Actions */}
          <div style={{ padding: '6px 0' }}>
            <Link
              to="/screenshots"
              role="menuitem"
              style={{ ...menuItem(false), textDecoration: 'none', display: 'block' }}
              onClick={() => setOpen(false)}
            >
              🖼 Screenshot Manager
            </Link>
            <button
              role="menuitem"
              style={menuItem(false)}
              onClick={() => { setOpen(false); setModal('changePassword'); }}
            >
              🔑 Change Password
            </button>
            <button
              role="menuitem"
              style={menuItem(true)}
              onClick={() => { setOpen(false); setModal('deleteAccount'); }}
            >
              🗑 Delete Account
            </button>
          </div>

          <div style={{ borderTop: `1px solid ${colors.borderColor}`, padding: '6px 0' }}>
            <button role="menuitem" style={menuItem(false)} onClick={handleLogout}>
              ← Log out
            </button>
          </div>
        </div>
      )}

      {/* ── Change Password modal ── */}
      {modal === 'changePassword' && (
        <div
          style={common.overlay}
          role="dialog"
          aria-modal="true"
          aria-labelledby="change-pw-title"
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div style={{ ...common.modal, maxWidth: 440 }}>
            <h2 id="change-pw-title" style={common.modalTitle}>Change Password</h2>
            {pwError && <p role="alert" style={common.errorText}>{pwError}</p>}
            <form onSubmit={handleChangePassword}>
              <label style={common.label}>Current Password</label>
              <input
                type="password" style={common.input} value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                required autoFocus placeholder="••••••••"
              />
              <label style={common.label}>New Password</label>
              <input
                type="password" style={common.input} value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                required placeholder="At least 8 characters"
              />
              <label style={common.label}>Confirm New Password</label>
              <input
                type="password" style={{ ...common.input, marginBottom: 0 }} value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                required placeholder="••••••••"
              />
              <div style={{ ...common.modalActions, marginTop: 24 }}>
                <button type="button" style={{ ...common.btn, ...common.btnGhost }} onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" style={{ ...common.btn, ...common.btnPrimary }} disabled={pwLoading}>
                  {pwLoading ? 'Saving…' : 'Change Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Delete Account modal ── */}
      {modal === 'deleteAccount' && (
        <div
          style={common.overlay}
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-account-title"
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div style={{ ...common.modal, maxWidth: 440 }}>
            <h2 id="delete-account-title" style={{ ...common.modalTitle, color: '#ef4444' }}>
              Delete Account
            </h2>
            <p style={{ color: colors.textSecondary, fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>
              This will permanently delete your account along with <strong style={{ color: colors.textPrimary }}>all your simulations, screens, hotspots, and packages</strong>. There is no way to recover this data.
            </p>
            {deleteError && <p role="alert" style={common.errorText}>{deleteError}</p>}
            <form onSubmit={handleDeleteAccount}>
              <label style={common.label}>Enter your password to confirm deletion</label>
              <input
                type="password" style={{ ...common.input, marginBottom: 0 }} value={deletePw}
                onChange={(e) => setDeletePw(e.target.value)}
                required autoFocus placeholder="••••••••"
              />
              <div style={{ ...common.modalActions, marginTop: 24 }}>
                <button type="button" style={{ ...common.btn, ...common.btnGhost }} onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" style={{ ...common.btn, ...common.btnDanger }} disabled={deleteLoading}>
                  {deleteLoading ? 'Deleting…' : 'Delete My Account'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
